import { useEffect, useRef } from 'react';
import { readStorage, STORAGE_KEYS, writeStorage } from '@/shared/lib/storage';

export interface IdleTimerOptions {
  /** Сколько бездействия до вызова onIdle (ПОРТ-22 и ADR-0017: 15 минут). */
  timeoutMs: number;
  onIdle: () => void;
  enabled?: boolean;
  /** Как часто проверять (по умолчанию 15 с). */
  checkIntervalMs?: number;
  /** Синхронизировать активность между вкладками через localStorage. */
  shareAcrossTabs?: boolean;
  now?: () => number;
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;
const WRITE_THROTTLE_MS = 5_000;

/**
 * Таймер бездействия: действия пользователя в любой вкладке продлевают сессию;
 * после сна компьютера проверка срабатывает сразу при возвращении на вкладку.
 */
export function useIdleTimer({ timeoutMs, onIdle, enabled = true, checkIntervalMs = 15_000, shareAcrossTabs = true, now = Date.now }: IdleTimerOptions): void {
  const lastActivity = useRef(now());
  const lastWrite = useRef(0);
  const onIdleRef = useRef(onIdle);
  const nowRef = useRef(now);
  useEffect(() => {
    onIdleRef.current = onIdle;
    nowRef.current = now;
  }, [onIdle, now]);

  useEffect(() => {
    if (!enabled) return;
    lastActivity.current = nowRef.current();

    const markActive = () => {
      const current = nowRef.current();
      lastActivity.current = current;
      if (shareAcrossTabs && current - lastWrite.current > WRITE_THROTTLE_MS) {
        lastWrite.current = current;
        writeStorage(STORAGE_KEYS.lastActivity, String(current));
      }
    };

    const check = () => {
      if (shareAcrossTabs) {
        const shared = Number(readStorage(STORAGE_KEYS.lastActivity));
        if (Number.isFinite(shared) && shared > lastActivity.current) lastActivity.current = shared;
      }
      if (nowRef.current() - lastActivity.current >= timeoutMs) onIdleRef.current();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEYS.lastActivity && event.newValue) {
        const shared = Number(event.newValue);
        if (Number.isFinite(shared) && shared > lastActivity.current) lastActivity.current = shared;
      }
    };

    for (const type of ACTIVITY_EVENTS) window.addEventListener(type, markActive, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    const interval = window.setInterval(check, checkIntervalMs);
    return () => {
      for (const type of ACTIVITY_EVENTS) window.removeEventListener(type, markActive, { capture: true });
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      window.clearInterval(interval);
    };
  }, [enabled, timeoutMs, checkIntervalMs, shareAcrossTabs]);
}
